import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * Ratio, rate and percent — the Grade 6 spine.
 *
 * This is the year comparison stops being about *difference* ("how many more?")
 * and becomes about *proportion* ("how many times?"). Every lesson here leans on
 * that shift, because students who miss it treat ratios as subtraction problems
 * for years afterwards.
 */
export const RATIO_LESSONS: Lesson[] = [
  lesson({
    id: 'ratios-01',
    unitId: 'unit-ratios',
    conceptId: 'ratios',
    title: 'Ratios',
    summary: 'Comparing two quantities by division rather than by difference.',
    objectives: [
      'Write and read a ratio in the forms 3:2 and "3 to 2"',
      'Simplify a ratio, and generate equivalent ratios',
      'Distinguish a part-to-part ratio from a part-to-whole fraction',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'A different kind of comparison',
        blocks: [
          text('A recipe uses 3 cups of flour to 2 cups of sugar. How would you describe that relationship?'),
          text('You could say "one more cup of flour than sugar" — that is a comparison by **difference**. But it is the wrong description, because doubling the recipe gives 6 and 4, and now the difference is 2. The relationship did not change; your description did.'),
          math('3 : 2'),
          text('A **ratio** compares by division instead. Flour to sugar is 3 to 2, and it stays 3 to 2 at 6 and 4, at 30 and 20, at any size you like.'),
        ],
      },
      intuition: {
        title: 'Ratios scale; differences do not',
        blocks: [
          text('That is the entire reason ratios exist. A recipe, a map scale, a mixture of paint, the shape of a photograph — all of these must survive being made bigger or smaller, and only a multiplicative comparison does.'),
          insight('Equivalent ratios work exactly like equivalent fractions: multiply or divide both parts by the same number and the relationship is unchanged. 3:2 = 6:4 = 15:10.'),
          warning('A ratio of 3:2 does **not** mean three-fifths and two-fifths of nothing in particular — but it does not mean 3/2 of the whole either. There are 3 + 2 = 5 parts altogether, so flour is 3 out of 5 of the mixture. Confusing part-to-part with part-to-whole is the classic ratio error.', 'The trap'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'tape-diagram', rows: [{ label: 'Flour', parts: 3 }, { label: 'Sugar', parts: 2 }], unitLabel: 'each block is one cup' },
            'Five blocks in total: three of them flour, two of them sugar.',
          ),
          text('The picture makes the part-to-whole reading obvious: flour is 3 of the 5 blocks, so 3/5 of the mixture.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A class has 12 girls and 18 boys. Write the ratio of girls to boys in simplest form, and say what fraction of the class is girls.'),
          steps([
            { label: 'Write the ratio in the order asked.', latex: '12 : 18' },
            { label: 'Divide both parts by their greatest common factor.', latex: '\\gcd(12, 18) = 6' },
            { label: 'Simplify.', latex: '12 : 18 = 2 : 3' },
            { label: 'For the fraction, use the total number of parts.', latex: '12 + 18 = 30' },
            { label: 'Girls as a fraction of the class.', latex: '\\frac{12}{30} = \\frac{2}{5}' },
          ]),
          tip('Order matters. "Girls to boys" is 2:3; "boys to girls" is 3:2. They are different statements about the same class.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('ratios', 4)] },
      independent: { title: 'On your own', blocks: [practice('ratios', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('ratios', 3, 'Working backwards from a ratio to the quantities.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A ratio compares by division, so it survives scaling.',
            'Simplify by dividing both parts by their greatest common factor.',
            'Order matters: 2:3 is not 3:2.',
            'For a fraction of the whole, add the parts to get the total.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'unit-rates-01',
    unitId: 'unit-ratios',
    conceptId: 'unit-rates',
    title: 'Rates and Unit Rates',
    summary: 'A ratio between different units — and why "per one" is what makes any two offers comparable.',
    objectives: [
      'Recognise a rate as a ratio between different units',
      'Compute a unit rate by dividing',
      'Use unit rates to compare and to solve problems',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Which is the better buy?',
        blocks: [
          text('One shop sells 3 kg of rice for £7.50. Another sells 5 kg for £12.00.'),
          text('You cannot compare those directly — different amounts, different prices. But divide each one down to a single kilogram and the comparison becomes trivial. That is a **unit rate**.'),
          math('\\frac{£7.50}{3\\text{ kg}} = £2.50\\text{ per kg} \\qquad \\frac{£12.00}{5\\text{ kg}} = £2.40\\text{ per kg}'),
        ],
      },
      intuition: {
        title: '"Per" means divide',
        blocks: [
          text('A **rate** is a ratio comparing two *different* units: miles and hours, pounds and kilograms, words and minutes. A **unit rate** is that rate with the second quantity reduced to one.'),
          insight('The word "per" always means division, and it tells you the order. Miles per hour is miles ÷ hours. Pounds per kilogram is pounds ÷ kilograms. Read the phrase and you have the calculation.'),
          warning('Divide the wrong way round and you get a real number that answers a different question. Hours per mile is a perfectly sensible quantity — it is just not speed.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          table(['Rate', 'As a division', 'Unit rate'], [
            ['150 miles in 3 hours', '150 ÷ 3', '50 miles per hour'],
            ['£7.50 for 3 kg', '7.50 ÷ 3', '£2.50 per kg'],
            ['240 words in 4 minutes', '240 ÷ 4', '60 words per minute'],
          ]),
          figure(
            { kind: 'tape-diagram', rows: [{ label: 'Shop A', parts: 3 }, { label: 'Shop B', parts: 5 }], unitLabel: 'kilograms bought' },
            'Different quantities, so the totals cannot be compared until both are reduced to one kilogram.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A car travels 210 miles in 3.5 hours. What is its speed, and how far would it go in 5 hours at that rate?'),
          steps([
            { label: 'Speed is miles per hour, so divide miles by hours.', latex: '210 \\div 3.5' },
            { label: 'Compute the unit rate.', latex: '60 \\text{ miles per hour}' },
            { label: 'Once you have the rate per one hour, scaling is multiplication.', latex: '60 \\times 5' },
            { label: 'Answer.', latex: '300 \\text{ miles}' },
          ]),
          tip('This is why unit rates are worth the extra step: once you know the value of one, every other quantity is a single multiplication away.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('unit-rates', 4)] },
      independent: { title: 'On your own', blocks: [practice('unit-rates', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('unit-rates', 3, 'Comparing two offers, and scaling up from a rate.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A rate compares different units; a unit rate is "per one".',
            '"Per" means divide, and names the order of the division.',
            'Unit rates make unlike quantities comparable.',
            'Once you know the rate for one, multiply to get any amount.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'percentages-01',
    unitId: 'unit-percent',
    conceptId: 'percentages',
    title: 'Percentages',
    summary: 'A ratio out of one hundred — which is what makes unlike things comparable at a glance.',
    objectives: [
      'Convert between percentages, fractions and decimals',
      'Find a percentage of a quantity',
      'Explain why percentages make comparison easy',
    ],
    estimatedMinutes: 30,
    sections: {
      introduction: {
        title: 'Everything out of a hundred',
        blocks: [
          text('You scored 17 out of 20 on one test and 43 out of 50 on another. Which went better?'),
          text('Hard to say — until both are rewritten out of the same number. Choose 100, and the answer is immediate: 85% and 86%.'),
          math('\\frac{17}{20} = \\frac{85}{100} = 85\\% \\qquad \\frac{43}{50} = \\frac{86}{100} = 86\\%'),
          text('"Per cent" is Latin for "per hundred". A percentage is just a ratio with the denominator fixed at 100 so that every comparison is a fair one.'),
        ],
      },
      intuition: {
        title: 'Three ways to write one number',
        blocks: [
          text('A percentage, a fraction and a decimal are three notations for the same quantity. Moving between them is not a conversion so much as a rewording.'),
          table(['Percent', 'Fraction', 'Decimal'], [
            ['50%', '1/2', '0.5'],
            ['25%', '1/4', '0.25'],
            ['10%', '1/10', '0.1'],
            ['75%', '3/4', '0.75'],
            ['1%', '1/100', '0.01'],
          ]),
          warning('Before calculating with a percentage, convert it. 25% of 80 is not 25 × 80. It is 0.25 × 80 = 20, or equivalently a quarter of 80.', 'The trap'),
          insight('To go from percent to decimal, divide by 100 — the digits shift two places right. To go back, multiply by 100.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure({ kind: 'grid', width: 10, height: 10 }, 'A hundred squares. Shading 25 of them is 25%, a quarter, 0.25 — one amount, three names.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A jacket costs £80 and is reduced by 15%. What do you pay?'),
          steps([
            { label: 'Convert the percentage to a decimal.', latex: '15\\% = 0.15' },
            { label: 'Find the discount: "of" means multiply.', latex: '0.15 \\times 80 = 12' },
            { label: 'Subtract it from the original price.', latex: '80 - 12 = 68' },
            { label: 'Answer.', latex: '£68' },
          ]),
          tip('There is a shortcut worth knowing: paying after a 15% discount means paying 85% of the price, so 0.85 × 80 = 68 in one step.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('percentages', 4)] },
      independent: { title: 'On your own', blocks: [practice('percentages', 5)] },
      challenge: { title: 'Something harder', blocks: [practice('percentages', 3, 'Discounts, increases, and finding the percentage itself.')] },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'A percentage is a number of hundredths.',
            'Convert to a decimal or fraction before calculating.',
            '"Of" means multiply.',
            'Percentages make unlike quantities comparable, which is their whole purpose.',
          ]),
        ],
      },
    },
  }),
];
